<template>
	<v-dialog v-model="showDialog" persistent width="70vw" content-class="no-max-height" id="addLocationWizard">
		<v-card style="height: 95vh">
			<v-card-title class="card-header text-left fixed-card-title2">
				<v-row no-gutters>
					<v-col>
						<v-toolbar-title>{{ $t('onboarding-wizard') }}</v-toolbar-title>
					</v-col>
					<v-spacer />
					<v-icon
						@click="closeWizardDialog(0)"
						class="white-icon"
						:disabled="currentStep > 0 && currentStep < onboardingSteps.length - 1">
						mdi-close
					</v-icon>
				</v-row>
			</v-card-title>
			<v-card-text class="pa-0 overflow-y-hidden d-flex" style="height: calc(100% - 62px)">
				<div
					class="d-flex flex-column align-center justify-start"
					style="background-color: #f9fafb; width: 300px; height: 100%">
					<v-timeline dense class="align-left">
						<v-timeline-item
							v-for="(step, index) in onboardingSteps"
							:key="index"
							:icon="index < currentStep ? 'mdi-check' : ''"
							:color="index < currentStep ? 'green' : 'primary'"
							class="my-3"
							small
							fill-dot>
							<v-chip
								v-if="currentStep === index"
								color="primary"
								class="my-4 white--text text-left text-no-wrap"
								label
								style="font-weight: bold">
								{{ step.title }}
							</v-chip>
							<p v-else class="text-left pa-0 pl-2 ma-0 text-no-wrap" style="font-weight: bold">
								{{ step.title }}
							</p>
						</v-timeline-item>
					</v-timeline>
				</div>
				<v-card style="width: calc(100% - 300px)" tile>
					<v-card-text class="pa-0 overflow-y-auto" style="height: calc(100% - 60px)">
						<HospitalAdd
							v-if="currentStep === 0"
							style="height: 100%"
							ref="hospitalAddWizard"
							wizardMode
							@update:wizard-valid="updateWizardValid"
							@update:createdFacility="storeFacility"
							:dataProps="dataProps" />

						<MedicalStaffAssign
							v-else-if="currentStep === 1"
							ref="medicalStaffAddWizard"
							wizardMode
							:wizardData="facilityData"
							@update:selectedMedicalStaff="updateSelectedMedicalStaff"
							name="medicalStaffAssignWizard" />

						<MedicalNoteCategoryCopyTemplate
							v-else-if="currentStep === 2"
							ref="MedicalNoteCategoryCopyTemplate"
							wizardMode
							:wizardData="facilityData"
							@update:categoriesDestinationItems="updateCategoriesDestinationItems"
							@update:hospitalsDestination="updatedHospitalsDestination"
							name="medicalStaffAssignWizard"
							:dataPros="dataProps" />

						<div v-else-if="currentStep === 3" class="pa-4 pb-2 overflow-y-auto" style="height: 100%">
							<v-row dense>
								<v-col cols="12" v-for="(staff, index) in medicalStaffs" :key="index">
									<v-card :disabled="index < medicalStaffs - 1">
										<WizardOnboardingMedicalStaffAdd
											:ref="'WizardOnboardingMedicalStaffAdd'"
											:key="index"
											wizardMode
											:wizardData="facilityData"
											@update:addStaffResponse="addStaffResponse"
											@update:firstName="updatedFirstName"
											@update:lastName="updatedLastName"
											@update:email="updatedEmail"
											@update:validateEmail="validateEmail"
											:name="`WizardOnboardingMedicalStaffAdd_${index}`"
											:id="`WizardOnboardingMedicalStaffAdd_${index}`" />
									</v-card>
								</v-col>
							</v-row>
						</div>

						<div
							v-else-if="currentStep === 4"
							class="pa-4 pb-2 overflow-y-auto d-flex align-center justify-center"
							style="height: 100%">
							<h2>{{ $t('wizard-complete') }}</h2>
						</div>
					</v-card-text>
					<div class="d-flex justify-end pa-4 pt-2">
						<v-btn outlined v-if="currentStep === 3" @click="saveAndAddMoreStaff" :disabled="!canAddStaff" class="mr-4">
							{{ $t('save-and-add-more-staff') }}
						</v-btn>
						<v-btn
							color="primary"
							width="70px"
							@click="nextStep"
							:disabled="!stepPassedValidation"
							v-if="currentStep < 4">
							{{ $t('next') }}
						</v-btn>
						<v-btn color="primary" width="70px" @click="closeWizardDialog" v-else>
							{{ $t('close') }}
						</v-btn>
					</div>
				</v-card>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import HospitalAdd from '@/views/DrawerDialogs/HospitalAdd';
import MedicalStaffAssign from '@/views/DrawerDialogs/MedicalStaffAssign.vue';
import MedicalNoteCategoryCopyTemplate from '../DrawerDialogs/MedicalNoteCategoryCopyTemplate.vue';
import WizardOnboardingMedicalStaffAdd from '../DrawerDialogs/WizardOnboardingMedicalStaffAdd.vue';

export default {
	name: 'OnboardingWizardDialog',
	props: {
		showDialog: {
			type: Boolean,
			required: true,
		},
		dataProps: {
			type: Object,
			required: false,
			default: null,
		},
	},
	components: { HospitalAdd, MedicalStaffAssign, MedicalNoteCategoryCopyTemplate, WizardOnboardingMedicalStaffAdd },
	data () {
		return {
			facilityData: {},
			stepPassedValidation: false,
			onboardingSteps: [
				{ title: this.$t('create-new-facility') },
				{ title: this.$t('assign-staff') },
				{ title: this.$t('assign-templates') },
				{ title: this.$t('generic-caregivers') },
				{ title: this.$t('wizard-completed') },
			],
			currentStep: 0,
			medicalStaffs: 1,
			canAddStaff: false,
			firstNameCurrent: '',
			lastNameCurrent: '',
			emailCurrent: '',
			emailValidateCurrent: false,
		};
	},
	methods: {
		addMoreStaff () {
			this.medicalStaffs++;
		},
		closeWizardDialog (currentStep = 0) {
			this.facilityData = {};
			this.stepPassedValidation = false;
			this.$emit('closeWizardDialog', currentStep);
		},
		storeFacility (facility) {
			this.facilityData = facility;
			this.$emit('update:createdFacility', facility);
			this.currentStep++;
		},

		updateWizardValid (isValid) {
			this.stepPassedValidation = isValid;
		},

		nextStep () {
			let staff;

			switch (this.currentStep) {
				case 0:
					this.$refs.hospitalAddWizard.saveHospital().then((res) => {});
					break;
				case 1:
					this.stepPassedValidation = false;
					this.currentStep++;
					break;
				case 2:
					this.stepPassedValidation = false;
					this.currentStep++;
					break;
				case 3:
					if (this.firstNameCurrent.length > 0) {
						staff = this.$refs.WizardOnboardingMedicalStaffAdd[this.medicalStaffs - 1];
						staff.saveStaff().then((res) => {
							if (res.resFlag) {
								this.currentStep++;
							}
						});
					} else {
						this.currentStep++;
					}
					break;
				case 4:
					this.closeWizardDialog(this.currentStep);
					break;
			}
		},

		updateSelectedMedicalStaff (val) {
			this.stepPassedValidation = val.length > 0;
		},
		updateCategoriesDestinationItems (val) {
			this.stepPassedValidation = val.length > 0;
		},
		addStaffResponse (val) {
			if (val.id > 0) {
				this.addMoreStaff();
			}
		},
		async saveAndAddMoreStaff () {
			const staff = this.$refs.WizardOnboardingMedicalStaffAdd[this.medicalStaffs - 1];
			await staff.saveStaff();
		},
		updatedFirstName (val) {
			this.firstNameCurrent = val;
			this.stepPassedValidation =
				val.length > 0 && this.lastNameCurrent.length > 0 && this.emailCurrent.length > 0 && this.emailValidateCurrent;
			this.canAddStaff =
				this.firstNameCurrent.length > 0 &&
				this.lastNameCurrent.length > 0 &&
				this.emailCurrent.length > 0 &&
				this.emailValidateCurrent;
		},
		updatedEmail (val) {
			this.emailCurrent = val;
			this.stepPassedValidation =
				this.firstNameCurrent.length > 0 &&
				this.lastNameCurrent.length > 0 &&
				val.length > 0 &&
				this.emailValidateCurrent;
			this.canAddStaff =
				this.firstNameCurrent.length > 0 &&
				this.lastNameCurrent.length > 0 &&
				this.emailCurrent.length > 0 &&
				this.emailValidateCurrent;
		},
		updatedLastName (val) {
			this.lastNameCurrent = val;
			this.stepPassedValidation =
				this.firstNameCurrent.length > 0 && val.length > 0 && this.emailCurrent.length > 0 && this.emailValidateCurrent;
			this.canAddStaff =
				this.firstNameCurrent.length > 0 &&
				this.lastNameCurrent.length > 0 &&
				this.emailCurrent.length > 0 &&
				this.emailValidateCurrent;
		},
		updatedHospitalsDestination (val) {
			this.stepPassedValidation = val.length > 0;
		},
		validateEmail (email) {
			this.emailValidateCurrent = email;
			this.canAddStaff =
				this.firstNameCurrent.length > 0 && this.lastNameCurrent.length > 0 && this.emailCurrent.length > 0 && email;
			this.stepPassedValidation =
				this.firstNameCurrent.length > 0 && this.lastNameCurrent.length > 0 && this.emailCurrent.length > 0 && email;
		},
	},
};
</script>

<style>
.no-max-height {
	max-height: none !important;
}
</style>
